import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { getDragonAnnotation } from '../../utils/dragonAnnotations';
import type { KingdomDossierDto } from '../../types/dossier';
import type { KingdomRelationshipDto } from '../../types/dossier';

interface DossierHeaderProps {
  dossier: KingdomDossierDto;
  relationships: KingdomRelationshipDto[];
}

/**
 * Kingdom summary strip + war chips + dragon annotation.
 *
 * War chips route to the Dossier of the at-war kingdom.
 * Dragon annotation is display-only — no offense modifier is applied.
 */
export function DossierHeader({ dossier, relationships }: DossierHeaderProps) {
  const dragon = getDragonAnnotation(dossier.dragonType);
  const warChips = relationships.filter((r) => r.relationship === 'WAR');
  const MAX_INLINE = 3;

  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        padding: '12px 16px',
        marginBottom: 0,
      }}
    >
      {/* Kingdom name + stance + dragon */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ color: COLORS.text.primary, fontSize: 18, fontWeight: 700 }}>
          {dossier.kingdomName ?? dossier.kingdomId}
        </span>
        {/* SF-169: reciprocal link to the World Viewer's public roster (the reverse direction of
            DesktopKingdomViewerShell/MobileKingdomViewerShell's "View Dossier" link). */}
        <Link
          to={`/world/kingdoms/${dossier.kingdomId}`}
          style={{ color: COLORS.link, fontSize: 12, textDecoration: 'underline' }}
        >
          View public roster
        </Link>
        {dossier.kingdomStance && (
          <span
            style={{
              padding: '1px 8px',
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text.tertiary,
              fontSize: 12,
            }}
          >
            {dossier.kingdomStance}
          </span>
        )}
        {dossier.dragonType && dossier.dragonType !== 'NONE' && (
          <span
            style={{
              padding: '1px 8px',
              border: `1px solid ${COLORS.warning}`,
              color: COLORS.warning,
              fontSize: 12,
            }}
            title={dragon.economicEffect}
          >
            {dragon.label}
          </span>
        )}
      </div>

      {/* Stats row */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          marginTop: 8,
          color: COLORS.text.secondary,
          fontSize: 13,
          flexWrap: 'wrap',
        }}
      >
        {dossier.kingdomNetworth > 0 && (
          <span>
            <span style={{ color: COLORS.text.muted }}>NW: </span>
            <span style={{ color: COLORS.gold, fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(dossier.kingdomNetworth)}
            </span>
          </span>
        )}
        {dossier.kingdomLand > 0 && (
          <span>
            <span style={{ color: COLORS.text.muted }}>Land: </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(dossier.kingdomLand)}</span>
          </span>
        )}
        <span>
          <span style={{ color: COLORS.text.muted }}>Members: </span>
          <span>{dossier.provinces.length}</span>
        </span>
      </div>

      {/* War chips */}
      {warChips.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: COLORS.text.muted, fontSize: 12 }}>At war with:</span>
          {warChips.slice(0, MAX_INLINE).map((r) => (
            <Link
              key={r.kingdomId}
              to={`/world/dossier/${r.kingdomId}`}
              style={{
                padding: '2px 10px',
                border: `1px solid ${COLORS.negative}`,
                color: COLORS.negative,
                fontSize: 12,
                textDecoration: 'none',
                borderRadius: 2,
              }}
            >
              ⚔ {r.kingdomName ?? r.kingdomId}
            </Link>
          ))}
          {warChips.length > MAX_INLINE && (
            <span style={{ color: COLORS.text.muted, fontSize: 12 }}>
              +{warChips.length - MAX_INLINE} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}
